import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import './Hero.css';

const HERO_BG =
  'https://images.unsplash.com/photo-1451187580459-43490279c0fa?q=80&w=2072&auto=format&fit=crop';

const Hero: React.FC = () => {
  const { t } = useLanguage();
  const heroRef = useRef<HTMLElement>(null);
  const [parallaxY, setParallaxY] = useState(0);

  useEffect(() => {
    let frame = 0;

    const updateParallax = () => {
      if (!heroRef.current) return;
      const rect = heroRef.current.getBoundingClientRect();
      const heroHeight = heroRef.current.offsetHeight;
      const scrollProgress = Math.min(Math.max(-rect.top / heroHeight, 0), 1.5);
      setParallaxY(scrollProgress * heroHeight * 0.35);
    };

    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(updateParallax);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    updateParallax();

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  return (
    <section className="hero" id="home" ref={heroRef}>
      <svg className="hero-clip-defs" width="0" height="0" aria-hidden="true">
        <defs>
          <clipPath id="hero-bg-curve-clip" clipPathUnits="objectBoundingBox">
            <path d="M 0,0 H 1 V 0.904 C 0.667,0.995 0.333,0.995 0,0.904 Z" />
          </clipPath>
        </defs>
      </svg>

      <div
        className="hero-bg-wrapper"
        style={{ transform: `translate3d(0, ${parallaxY}px, 0)` }}
        aria-hidden="true"
      >
        <div
          className="hero-bg-image"
          style={{ backgroundImage: `url(${HERO_BG})` }}
        />
      </div>
      <div className="hero-overlay" aria-hidden="true" />

      <div className="hero-container editorial-container">
        <h1 className="hero-title">
          {t.hero.titleLines.map((line, index) => (
            <span
              key={`${line}-${index}`}
              className={`hero-title-line${
                index === t.hero.titleAccentIndex ? ' hero-title-line--accent' : ''
              }`}
            >
              {line}
            </span>
          ))}
        </h1>
        <p className="hero-subtitle">{t.hero.subtitle}</p>
        <div className="hero-actions">
          <a href="#contact" className="btn btn-primary">{t.hero.ctaPrimary}</a>
          <Link to="/services" className="btn btn-secondary hero-services-link">
            {t.hero.ctaSecondary}
            <ArrowUpRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default Hero;
